import TableData, { ColumnDefinition, tableConstants } from '../../../../components/tableView/TableData/TableData';

const arraySort = require('array-sort');

const dateUtil = require('../../../../utils/dateUtil/dateUtil');
const stringUtil = require('../../../../utils/string/stringUtil');
const formatter = require('../../../../utils/formatter/formatter');
const businessDateUtil = require('../../../../utils/dateUtil/businessDateUtil');
const stateManager = require('../../../../utils/state/stateManager');

const salesLoader = require('../../../../utils/domain/sales/salesLoader');
const saleCalculator = require('../../../../utils/domain/sales/saleCalculator');
const commonUtility = require('../../../../utils/domain/commonUtility');
const staffUtility = require('../../../../utils/domain/staff/staffUtility');
const saleCommonUtility = require('../../../../utils/domain/sales/saleCommonUtility');

const { columnAlignments } = tableConstants;


export async function loadDriverSalesData(currentDate, forceReloading)
{
    let sales;

    if (businessDateUtil.isDateSameAsTodayBusinessDate(currentDate))
    {
        // For todaySales, we invoke loadTodaysSalesAndSalesStatus only on forceReloading
        // else we get data from redux store

        if (forceReloading)
        {
            await salesLoader.loadTodaysSalesAndSalesStatus();
        }

        sales = stateManager.getStore().getState().todaySales;
    }
    else
    {
        sales = await salesLoader.loadSales(currentDate);
    }

    return sales.filter(sale => !saleCommonUtility.isSaleHold(sale) && saleCommonUtility.isPropertyDeliverySale(sale));
}

export function getDriverSalesSummary(driverSales)
{
    const salesSummary = [];

    driverSales.forEach(sale => 
    {
        const driverId = sale.driverId || "";           // driverId === "" is flag for Unassigned sale
        const driverName = getDriverName(driverId);

        let summary = salesSummary.find(summary => summary.driverId === driverId && summary.driverName === driverName);

        if (summary == null)
        {
            summary = {
                driverId, driverName,
                totalSalesCount: 0, totalSaleAmount: 0, totalPaidAmount: 0, totalDueAmount: 0,
                totalDeliveryCharges: 0, totalTipCharges: 0, totalCancelledSalesCount: 0
            }

            salesSummary.push(summary);
        }

        summary.totalSalesCount += 1;
        summary.totalSaleAmount += saleCalculator.calculateTotalNetSalePrice(sale);
        summary.totalPaidAmount += saleCalculator.calculateTotalPaidAmount(sale);
        summary.totalDueAmount += saleCalculator.calculateRemainingPayableAmount(sale);
        summary.totalDeliveryCharges += saleCalculator.calculateTotalDeliveryCharges(sale);
        summary.totalTipCharges += saleCalculator.calculateTotalTipCharges(sale);
        summary.totalCancelledSalesCount += saleCommonUtility.isCancelled(sale) ? 1 : 0;
    });

    return arraySort(salesSummary, "driverName");
}

export function getDriverSalesDetail(driverSales, driverId)
{
    // driverId === "" is flag for Unassigned sale

    return driverSales
        .filter(sale => (sale.driverId || "") === driverId)
        .map(sale =>
        {
            const dispatchInfo = getDispatchInfo(sale);

            return {
                saleId: sale.id,
                orderNumber: sale.number,
                orderTime: sale.dateTime,
                customerName: dispatchInfo && dispatchInfo.customerName,
                customerPhoneNumber: dispatchInfo && dispatchInfo.customerPhoneNumber,
                deliveryAddress: dispatchInfo && dispatchInfo.deliveryAddress,
                saleAmount: saleCalculator.calculateTotalNetSalePrice(sale),
                paidAmount: saleCalculator.calculateTotalPaidAmount(sale),
                dueAmount: saleCalculator.calculateRemainingPayableAmount(sale),
                deliveryCharges: saleCalculator.calculateTotalDeliveryCharges(sale),
                tipCharges: saleCalculator.calculateTotalTipCharges(sale),
                isFullyPaid: saleCalculator.isSaleFullyPaid(sale)
            }
        });
}

export function getDriverSaleSummaryTableData(saleSummary)
{
    const driverName = saleSummary.driverName;

    const columnDefinitions = [
        new ColumnDefinition(40),
        new ColumnDefinition(60, columnAlignments.alignRight)
    ];

    const tableData = new TableData(driverName, driverName, ...columnDefinitions);

    tableData.setHeaderRow("Driver Name", driverName);

    tableData.addDataRow("Total Deliveries", saleSummary.totalSalesCount);
    tableData.addDataRow("Cancelled Sales", saleSummary.totalCancelledSalesCount);
    tableData.addDataRow("Total Delivery Charges", formatter.convertToCurrencyFormat(saleSummary.totalDeliveryCharges));
    tableData.addDataRow("Total Tips", formatter.convertToCurrencyFormat(saleSummary.totalTipCharges));
    tableData.addDataRow("Total Sale Amount", formatter.convertToCurrencyFormat(saleSummary.totalSaleAmount));
    tableData.addDataRow("Total Paid Amount", formatter.convertToCurrencyFormat(saleSummary.totalPaidAmount));

    tableData.setFooterRow("Total Due Amount", formatter.convertToCurrencyFormat(saleSummary.totalDueAmount));

    return tableData;
}

export function getDriverSaleDetailTableData(saleDetailItem)
{
    const orderNumber = saleDetailItem.orderNumber;

    const columnDefinitions = [
        new ColumnDefinition(40),
        new ColumnDefinition(60, columnAlignments.alignRight)
    ];

    const tableData = new TableData(orderNumber, orderNumber, ...columnDefinitions);

    tableData.setHeaderRow("Order Number", orderNumber);

    tableData.addDataRow("Order Time", dateUtil.formatDate(dateUtil.convertToLocalStandard(saleDetailItem.orderTime), dateUtil.getHourMinuteFormat()));
    tableData.addDataRow("Customer Name", saleDetailItem.customerName);
    tableData.addDataRow("Phone Number", saleDetailItem.customerPhoneNumber);
    tableData.addDataRow("Delivery Address", saleDetailItem.deliveryAddress);
    tableData.addDataRow("Delivery Charges", formatter.convertToCurrencyFormat(saleDetailItem.deliveryCharges));
    tableData.addDataRow("Tips Amount", formatter.convertToCurrencyFormat(saleDetailItem.tipCharges));
    tableData.addDataRow("Sale Amount", formatter.convertToCurrencyFormat(saleDetailItem.saleAmount));
    tableData.addDataRow("Paid Amount", formatter.convertToCurrencyFormat(saleDetailItem.paidAmount));

    tableData.setFooterRow("Due Amount", formatter.convertToCurrencyFormat(saleDetailItem.dueAmount));

    return tableData;
}

export function getDriverName(driverId)
{
    let driverName = "Unassigned";

    if (!stringUtil.isStringNullOrEmpty(driverId))
    {
        const driver = staffUtility.getStaffById(driverId);
        if (driver != null) driverName = staffUtility.getStaffCompleteName(driver);
    }

    return driverName;
}


function getDispatchInfo(sale)
{
    let dispatchInfo = null;

    try
    {
        dispatchInfo = JSON.parse(sale.dispatchInfo);
    }
    catch { }

    return dispatchInfo;
}